import { useState, useRef, useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'

/**
 * Combines useState and useRef into a single hook, keeping both in sync.
 * Use this when event handler closures need the latest state value without
 * re-registering on every render.
 */
export function useStateRef<T>(
  initial: T
): [T, Dispatch<SetStateAction<T>>, MutableRefObject<T>] {
  const [state, setState] = useState<T>(initial)
  const ref = useRef<T>(initial)

  const setStateAndRef: Dispatch<SetStateAction<T>> = useCallback((value) => {
    if (typeof value === 'function') {
      setState((prev) => {
        const next = (value as (prev: T) => T)(prev)
        ref.current = next
        return next
      })
    } else {
      ref.current = value
      setState(value)
    }
  }, [])

  return [state, setStateAndRef, ref]
}
